<template>
  <page-container>
    <template v-slot:page-fixed-header>
      <v-col cols="12" sm="10" md="10" class="pl-2" v-if="isDataLoading">
        <v-skeleton-loader type="heading" class="text-h3"></v-skeleton-loader>
      </v-col>
      <span
        v-else
        class="text-h5 font-weight-bold ml-3 full-width page-header"
        >{{
          surveyType === "copy"
            ? $t("poll.lblCreatePoll")
            : routeId
            ? $t("poll.lblEditPoll")
            : $t("poll.lblCreatePoll")
        }}</span
      >
      <v-spacer></v-spacer>
      <v-col cols="12" sm="2" md="2" class="pl-2" v-if="isDataLoading">
        <v-skeleton-loader type="heading" class="text-h3"></v-skeleton-loader>
      </v-col>
      <v-tooltip bottom v-if="!isDataLoading">
        <template v-slot:activator="{ on, attrs }">
          <a-btn
            v-if="!isDataLoading"
            class="ml-2"
            color="primaryDark"
            @click.stop="savePoll"
            :disabled="!isDirty || isSurveyExpired"
            v-bind="attrs"
            v-on="on"
          >
            <v-icon size="16" class="mr-3">mdi-content-save</v-icon
            >{{ $t("btnSave") }}</a-btn
          >
        </template>
        <span>{{ $t("btnSave") }} </span>
      </v-tooltip>
      <CloseIcon v-if="!isDataLoading" @click="closePoll" classProps="ml-2" />
    </template>
    <template v-slot:page-content>
      <v-col cols="12" sm="12" md="12" class="pa-0" v-if="isDataLoading">
        <v-card flat>
          <v-card-text>
            <v-container>
              <v-skeleton-loader
                type="image,list-item-two-line, list-item-two-line, article, list-item"
              ></v-skeleton-loader>
            </v-container>
          </v-card-text>
        </v-card>
      </v-col>
      <v-card v-else class="mt-3 mb-3" tile>
        <v-card-text>
          <v-form ref="refCreatePollForm" v-model="valid" lazy-validation>
            <v-row>
              <v-col cols="12" sm="12" md="12">
                <a-text-field
                  class="titleField"
                  dense
                  v-model="pollDetails.title"
                  :requiredMessage="$t('poll.lblSurveyTitleReq')"
                  required
                  :label="$t('poll.lblPollTitle')"
                  :disabled="isSurveyExpired"
                ></a-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="12" md="12">
                {{ $t("poll.lblDescription") }}:
                <div
                  id="resize"
                  style="border: solid 1px lightgray; padding-bottom: 1px"
                >
                  <a-rich-text-editor
                    v-model="pollDescription"
                    :editorReadonly="isSurveyExpired"
                  ></a-rich-text-editor>
                </div>
              </v-col>
            </v-row>
            <v-row justify="center">
              <v-col cols="12" sm="12" md="8" lg="8">
                <date-time-range-picker
                  :startDate.sync="pollDetails.startDate"
                  :endDate.sync="pollDetails.endDate"
                  :endDateAutoIncrease="1440"
                ></date-time-range-picker>
              </v-col>
              <v-col cols="12" sm="12" md="4" lg="4">
                <v-switch
                  dense
                  hide-details
                  :label="lblAnonymous"
                  v-model="pollDetails.isAnonymous"
                  :disabled="isSurveyExpired"
                ></v-switch>
              </v-col>
            </v-row>
            <div
              class="mt-5"
              v-for="question in pollDetails.questions"
              :key="question.id"
            >
              <component
                v-if="loadTypeComponent()"
                :is="loadTypeComponent()"
                :value="question"
                survey-type="poll"
                :responseQuestion="responseQuestion"
              ></component>
            </div>
          </v-form>
        </v-card-text>
      </v-card>
      <message-sheet
        :showMessage="displayMessage"
        :message="message"
        :messageType="messageType"
        @close="displayMessage = false"
      ></message-sheet>
      <prevent-navigation
        :prevent="isDirty && !isSaved"
        @save="saveOnLeave"
        @discard="$event.next()"
        @cancel="$event.next(false)"
      >
      </prevent-navigation>
    </template>
  </page-container>
</template>

<script>
import DateTimeRangePicker from "../DateTimeRangePicker.vue";
import _ from "lodash";
import { createUUID } from "../../utils/util";
import { mapActions, mapMutations, mapState, mapGetters } from "vuex";
import OptionForm from "./OptionForm.vue";
import typeComponents from "../poll/components/index";
import Loader from "../Loader.vue";
import Vue from "vue";
import SignalRMixin from "../../mixin/SignalRMixin";
import PreventNavigation from "@/components/PreventNavigation.vue";
import MessageSheet from "../MessageSheet.vue";

import {
  RichTextEditorPlugin,
  Toolbar,
  Link,
  Count,
  HtmlEditor,
  QuickToolbar,
  Table,
  Resize,
} from "@syncfusion/ej2-vue-richtexteditor";
Vue.use(RichTextEditorPlugin);

export default {
  name: "CreatePoll",
  components: {
    DateTimeRangePicker,
    OptionForm,
    Loader,
    PreventNavigation,
    MessageSheet,
  },
  mixins: [SignalRMixin],
  provide: {
    richtexteditor: [
      Toolbar,
      Link,
      Count,
      HtmlEditor,
      QuickToolbar,
      Resize,
      Table,
    ],
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      originalPollData: {},
      isDataLoading: false,
      valid: true,
      options: [],
      optionLabel: "Option",
      startDate: new Date(),
      endDate: new Date(),
      valid: false,
      routeId: null,
      loaderLoading: false,
      loadingMessage: this.$t("LoaderText"),
      richtexteditorEnable: true,
      isSaved: false,
      newQuestion: {
        number: null,
        question: "",
        options: [
          { text: "", value: "", fill: "" },
          { text: "", value: "", fill: "" },
        ],
        isMandatory: false,
        isMultipleAnswer: false,
        type: "choice",
        id: null,
        isEdit: true,
        selectedAnswer: null,
        subtitle: "",
        isSubtitle: false,
      },
      newQuestionRef: {
        number: null,
        question: "",
        options: [
          { text: "", value: "", fill: "" },
          { text: "", value: "", fill: "" },
        ],
        isMandatory: false,
        isMultipleAnswer: false,
        type: "choice",
        id: null,
        isEdit: true,
        selectedAnswer: null,
        subtitle: "",
        isSubtitle: false,
      },
      pollQuestion: {
        number: null,
        question: "",
        options: [
          { text: "", value: createUUID() },
          { text: "", value: createUUID() },
        ],
        isMandatory: false,
        isMultipleAnswer: false,
        type: "choice",
        id: null,
        isEdit: true,
        selectedAnswer: null,
        subtitle: "",
        isSubtitle: false,
      },
      refPollQuestion: {
        number: null,
        question: "",
        options: [
          { text: "", value: createUUID() },
          { text: "", value: createUUID() },
        ],
        isMandatory: false,
        isMultipleAnswer: false,
        type: "choice",
        id: null,
        isEdit: true,
        selectedAnswer: null,
        subtitle: "",
        isSubtitle: false,
      },
      displayMessage: false,
      messageType: "",
      message: "",
    };
  },
  computed: {
    ...mapState({
      pollDetails: (state) => state.poll.surveyDetails,
      surveyResponseResult: (state) => state.poll.surveyResponseResult,
      refSurveyDetails: (state) => state.poll.refSurveyDetails,
    }),
    ...mapGetters({
      isSurveyExpired: "poll/isSurveyExpired",
    }),
    responseQuestion() {
      const questions = [];
      if (this.surveyResponseResult.length) {
        this.surveyResponseResult.forEach((item) => {
          item.questionAnswers.forEach((ques) => {
            questions.push({
              questionId: ques.questionId,
              answer: ques.answer,
            });
          });
        });
      }
      return questions;
    },
    isDirty() {
      let originalPollData = {
        ...this.originalPollData,
        description: this.originalPollData.description || "",
      };
      return (
        JSON.stringify(originalPollData) !== JSON.stringify(this.pollDetails)
      );
    },
    polleditable() {
      let startDate = new Date(this.pollDetails.startDate);
      let endDate = new Date(this.pollDetails.endDate);
      let today = new Date();
      if (this.pollDetails.id != null && endDate < today) {
        return false;
      } else if (
        this.pollDetails.id != null &&
        startDate >= today &&
        endDate <= today &&
        this.surveyResponseResult.length > 0
      ) {
        //active
        return false;
      } else if (
        this.pollDetails.id != null &&
        startDate <= today &&
        endDate >= today &&
        this.surveyResponseResult.length > 0
      ) {
        return false;
      } else {
        return true;
      }
    },
    pollButtonDisable() {
      let startDate = new Date(this.pollDetails.startDate);
      let endDate = new Date(this.pollDetails.endDate);
      let today = new Date();
      if (this.pollDetails.id != null && endDate > today) {
        return true;
      } else if (this.pollDetails.id != null && endDate <= today) {
        return false;
      } else {
        return true;
      }
    },
    pollDescription: {
      get() {
        return this.pollDetails.description ? this.pollDetails.description : "";
      },
      set(value) {
        this.pollDetails.description = value;
      },
    },
    lblAnonymous() {
      return this.$t("poll.lblAnonymous");
    },
    selectedTenant() {
      return sessionStorage.getItem("tenantId");
    },
    surveyType() {
      return this.$route.params.type || "";
    },
    lblCopy() {
      return this.$t("poll.tipCopy");
    },
  },
  async mounted() {
    const today = new Date();
    const startDate = new Date().toISOString();
    const endDate = new Date(today.getTime() + 1440 * 60000).toISOString();
    try {
      this.$refs.refCreatePollForm.resetValidation();
      this.$refs.refCreatePollForm.reset();
      this.resetSurveyDetails();
      this.setSurveyDetails({
        ...this.pollDetails,
        startDate: startDate,
        endDate: endDate,
      });
      let id = this.$route.params.id;
      if (this.$route.params.pollid && this.$route.params.pollid != "") {
        id = this.$route.params.pollid;
      }
      if (id && id !== "new") {
        this.routeId = id;
        this.signalRInit(`surveys_${this.selectedTenant}`);
        this.isDataLoading = true;
        if (this.$security.checkUserAccess("ViewSurveyPolls")) {
          await this.getSurveyById(id);
        }
        this.originalPollData = _.cloneDeep(this.pollDetails);
        this.isDataLoading = false;
        if (this.surveyType === "copy") {
          this.setSurveyDetails({
            ...this.pollDetails,
            id: null,
            title: `${this.lblCopy} ${this.pollDetails.title}`,
            startDate: startDate,
            endDate: endDate,
          });
        }
      } else {
        this.pollDetails.type = "poll";
        this.addDefaultQuestion();
      }
      const thisInstance = this;
      setTimeout(() => {
        if (thisInstance.$refs.rteObj) {
          thisInstance.$refs.rteObj.refreshUI();
        }
      }, 100);
    } catch (error) {
      this.isDataLoading = false;
    }
  },
  async created() {
    this.setSurveyResponseResult([]);
    this.resetSurveyDetails();
    const id = this.$route.params.id;
    if (
      id &&
      id !== "new" &&
      this.$security.checkUserAccess("ViewSurveyResponse")
    ) {
      await this.getResponseBySurveyId(id);
    }
  },
  methods: {
    ...mapActions({
      createSurvey: "poll/createSurvey",
      getSurveyById: "poll/getSurveyById",
      updateSurvey: "poll/updateSurvey",
      getResponseBySurveyId: "poll/getResponseBySurveyId",
    }),
    ...mapMutations({
      resetSurveyDetails: "poll/RESET_SURVEY_OBJECT",
      setSurveyDetails: "poll/GET_SURVEY_BY_ID",
      setSurveyResponseResult: "poll/GET_SURVEY_RESPONSE_RESULT",
    }),
    close,
    closePoll,
    createPoll,
    createUUID,
    savePoll,
    addNewPollDetails,
    updatePollDetails,
    addDefaultQuestion,
    navigateToOrigin,
    signalRInit,
    saveOnLeave,
    saveOnLeavePoll,
    saveData,
    showMessageSheet(message, messageType) {
      this.displayMessage = true;
      this.message = message;
      this.messageType = messageType;
    },
    onBlur: function (e) {
      this.$refs.rteObj.ej2Instances.toolbarSettings.enable = false;
    },
    onFocus: function (e) {
      this.$refs.rteObj.ej2Instances.toolbarSettings.enable = true;
    },
    loadTypeComponent() {
      return typeComponents["choice"] || null;
    },
  },
};

async function saveOnLeave({ next }) {
  if (this.$refs.refCreatePollForm.validate()) {
    if (this.isSurveyExpired) {
      this.showMessageSheet(
        this.$t("poll.msgSurveyExpired", { type: "Poll" }),
        "error"
      );
      return false;
    }
    await this.saveOnLeavePoll();
    next();
  }
}

function signalRInit(target) {
  this.signalConnection.on(target, async (message) => {
    const result = JSON.parse(message);
    if (result) {
      const id = this.$route.params.id;
      if (id) {
        if (this.$security.checkUserAccess("ViewSurveyPolls")) {
          await this.getSurveyById(id);
        }
        this.originalPollData = _.cloneDeep(this.pollDetails);
      }
      if (!this.pollDetails) {
        await this.$refs.confirm.open(
          this.$t("poll.lblPollDeleteWarning"),
          this.$t("poll.msgPollDeleteWarning"),
          { dialogIcon: "alert", noconfirm: true }
        );
        this.$router.push({ path: "/" });
      }
    }
  });
  if (this.signalConnection.connectionState.toLowerCase() === "disconnected") {
    this.signalConnection.start().catch();
  }
}

function addDefaultQuestion() {
  this.newQuestion = _.cloneDeep(this.newQuestionRef);
  this.newQuestion.number = this.pollDetails.questions.length + 1;
  this.newQuestion.id = createUUID();
  this.pollDetails.questions.push(this.newQuestion);
  this.originalPollData = _.cloneDeep(this.pollDetails);
}

async function saveOnLeavePoll() {
  this.richtexteditorEnable = false;
  if (this.$refs.refCreatePollForm.validate()) {
    await this.saveData();
  }
  this.richtexteditorEnable = true;
}

async function saveData() {
  this.loadingMessage = this.$t("SaveLoader");
  this.loaderLoading = true;
  const pollId = this.pollDetails.id;
  if (this.pollDetails.id != null) {
    await this.updatePollDetails().finally(() => (this.loaderLoading = false));
  } else {
    await this.addNewPollDetails().finally(() => (this.loaderLoading = false));
  }
  this.loadingMessage = this.LoaderText;
  this.$store.commit("setAlert", {
    variant: "success",
    message:
      pollId != null
        ? this.$t("poll.PollUpdateSuccess")
        : this.$t("poll.PollCreateSuccess"),
  });
}

async function savePoll() {
  this.richtexteditorEnable = false;
  if (this.$refs.refCreatePollForm.validate()) {
    this.isSaved = true;
    await this.saveData();
    this.close();
  }
  this.richtexteditorEnable = true;
}

async function addNewPollDetails() {
  this.pollDetails.id = createUUID();
  this.pollDetails.userid = this.$auth.user.tenantUserName;
  if (this.pollDetails.tenantid !== this.selectedTenant) {
    this.pollDetails.tenantid = this.selectedTenant;
  }
  this.pollDetails.createdDate = new Date().toISOString();
  await this.createSurvey(_.cloneDeep(this.pollDetails));
}

async function updatePollDetails() {
  await this.updateSurvey(_.cloneDeep(this.pollDetails));
}

function createPoll() {
  this.pollDetails.id = createUUID();
  this.pollDetails.userid = this.$auth.user.tenantUserName;
  this.pollDetails.createdDate = new Date();
  this.pollQuestion.number = this.pollDetails.questions.length + 1;
  this.pollQuestion.id = createUUID();
  this.pollDetails.title = this.pollQuestion.question;
  this.pollDetails.questions.push(this.pollQuestion);

  this.createSurvey(_.cloneDeep(this.pollDetails));
  this.close();
}
function closePoll() {
  this.navigateToOrigin();
}
function close() {
  this.$refs.refCreatePollForm.resetValidation();
  this.$refs.refCreatePollForm.reset();
  this.navigateToOrigin();
}

function navigateToOrigin() {
  switch (this.$route.params.origin) {
    case "dashboard":
      this.$router.push("/dashboard");
      break;
    case "survey":
      this.$router.push("/survey");
      break;
    case "calendar":
      this.$router.push("/calendar");
      break;
    default:
      this.$router.push("/");
      break;
  }
}
</script>

<style scoped>
.titleField {
  font-family: "Noto Sans JP" !important;
  font-style: normal !important;
  font-weight: 400 !important;
  font-size: 14px !important;
  line-height: 18px !important;
  min-height: 48px !important;
  max-height: 68px !important;
  color: var(--v-neutralGray-base) !important;
  align-items: center !important;
}
.titleField > .v-input__control > .v-input__slot {
  color: var(--v-neutralGray-base) !important;
  height: 48px !important;
}
.titleField /deep/ .v-input__slot {
  min-height: 48px !important;
}
::v-deep .e-richtexteditor .e-rte-character-count {
  z-index: 98 !important;
}
::v-deep .e-richtexteditor .e-resize-handle.e-south-east {
  z-index: 98 !important;
}
::v-deep .e-datetime-wrapper {
  height: 40px !important;
}
</style>
